import { RepertoireActions, RepertoireState, RepertoireActionTypes } from 'Types/RepertoireTypes';

export interface RepertoireReducer {
  repertoireReducer: RepertoireState;
}

// Read cursorVisible from its own localStorage key (not redux-persist)
// to prevent lifecycle dispatches (softRefreshRepertoire → false) from
// corrupting the persisted value during page unload.
function getPersistedCursorVisible(): boolean {
  try {
    const stored = localStorage.getItem('museflow_cursor_visible');
    if (stored === 'false') return false;
    return true; // default to true
  } catch {
    return true;
  }
}

function persistCursorVisible(visible: boolean): void {
  try {
    localStorage.setItem('museflow_cursor_visible', String(visible));
  } catch {
    // localStorage may be unavailable
  }
}

export const defaultState: RepertoireState = {
  repertoireData: [],
  userRepertoireProgresses: [],
  userRepertoireData: [],
  repertoireIdSelect: 1,
  repertoirePlaying: false,
  repertoireEnded: false,
  cursorVisible: getPersistedCursorVisible(),
  playSessionId: null,
  verticalScrollPositions: { home: 0, browse: 0 },
  horizontalScrollPositions: {},
};

export const repertoireReducer = (
  state = defaultState,
  action: RepertoireActions,
): RepertoireState => {
  // Override cursorVisible from the dedicated localStorage key after REHYDRATE.
  // redux-persist may have a stale value from lifecycle dispatches (softRefreshRepertoire
  // dispatches false during cleanup). The dedicated key is only written by user toggles.
  const actionAny = action as { type: string; payload?: Record<string, unknown> };
  if (actionAny.type === 'persist/REHYDRATE' && actionAny.payload) {
    const dedicatedValue = getPersistedCursorVisible();
    (actionAny.payload as Record<string, unknown>).cursorVisible = dedicatedValue;
  }
  switch (action.type) {
    case RepertoireActionTypes.SET_CURSOR_VISIBLE:
      return {
        ...state,
        cursorVisible: action.payload,
      };

    case RepertoireActionTypes.LOAD_REPERTOIRE_STATE:
      return {
        ...state,
        ...action.payload,
      };

    case RepertoireActionTypes.FETCH_REPERTOIRE_DATA_SUCCESS:
      return {
        ...state,
        repertoireData: action.payload,
      };

    case RepertoireActionTypes.SET_REPERTOIRE_ENDED:
      return {
        ...state,
        repertoireEnded: action.payload,
      };

    case RepertoireActionTypes.SET_REPERTOIRE_SELECT:
      return {
        ...state,
        repertoireIdSelect: action.payload.repertoireIdSelect,
        // currentUserLevelData: find(state.userLevelData, uld => uld.level.level_number === state.levelData[action.payload.levelSelect].level_number),
        // currentLevelTiers: find(state.tiersByLevels, tbl => tbl.level_number === state.levelData[action.payload.levelSelect].level_number)?.tiers
      };

    case RepertoireActionTypes.FETCH_URP_SUCCESS: {
      const current_urp = state.userRepertoireProgresses;
      const updatedArray = current_urp.slice();
      // This is used to "merge" the single-value array that gets passed with the put
      action.payload.forEach((payload_urp) => {
        const index = updatedArray.findIndex((state_urp) => state_urp.id === payload_urp.id);
        if (index !== -1) {
          updatedArray[index] = payload_urp;
        } else {
          updatedArray.push(payload_urp);
        }
      });

      return {
        ...state,
        userRepertoireProgresses: updatedArray,
      };
    }
    case RepertoireActionTypes.SET_REPERTOIRE_PLAYING:
      return {
        ...state,
        repertoirePlaying: action.payload.repertoirePlaying,
      };

    case RepertoireActionTypes.SET_PLAY_SESSION_ID:
      return {
        ...state,
        playSessionId: action.payload,
      };

    case RepertoireActionTypes.UPDATE_REPERTOIRE_DATA_LOCAL: {
      const urp = state.userRepertoireProgresses;
      if (urp) {
        const updatedArr = urp.slice();
        // This is used to "merge" the single-value array that gets passed with the put
        const payload_urp = action.payload;
        const index = updatedArr.findIndex((state_urp) => state_urp.id === payload_urp.id);
        if (index !== -1) {
          updatedArr[index] = { ...updatedArr[index], last_tempo: payload_urp.last_tempo };
        }
        return {
          ...state,
          userRepertoireProgresses: updatedArr,
        };
      } else {
        return state;
      }
    }

    case RepertoireActionTypes.CREATE_REPERTOIRE_SESSION:
      return state;

    case RepertoireActionTypes.FETCH_USER_REPERTOIRE_SUCCESS:
      return {
        ...state,
        userRepertoireData: action.payload,
      };

    case RepertoireActionTypes.SET_USER_REPERTOIRE_PROCESSING: {
      const updatedUserRepertoire = state.userRepertoireData.map((rep) =>
        rep.id === action.payload.id ? { ...rep, status: action.payload.status } : rep,
      );
      return {
        ...state,
        userRepertoireData: updatedUserRepertoire,
      };
    }

    case RepertoireActionTypes.SET_VERTICAL_SCROLL:
      return {
        ...state,
        verticalScrollPositions: {
          ...state.verticalScrollPositions,
          [action.payload.page]: action.payload.position,
        },
      };

    case RepertoireActionTypes.SET_HORIZONTAL_SCROLL:
      return {
        ...state,
        horizontalScrollPositions: {
          ...state.horizontalScrollPositions,
          [action.payload.rowId]: action.payload.position,
        },
      };

    default:
      return state;
  }
};
